import './poFormView.scss';
import { PoFormViewProps } from "./types/poFormViewProps";
import RegistrationNumber from './components/registrationNumber';
import ObservationConclusions from './components/observationConclusions';
import ObservationRecommendations from './components/observationRecommendations';
import { useContext, useEffect, useRef, useState } from 'react';
import { apiContext } from '../../../contexts/apiContext';
import { populateProtocolForm } from '../../../services/protocolFormService/protocolFormService';
import Spinner from '../../../components/spinner/spinner';
import { ClassNameSpinner } from '../../../components/spinner/types/spinnerProps';
import ElaboratorsAndInstitutions from '../shared/ElaboratorsAndInstitutions/elaboratorsAndInstitutions';
import PhotographicDocumentation from './components/photographicDocumentation';
import PoFormContext, { defaultPoForm, poFormTypes } from '../../../contexts/poFormContext';
import ProtocolPrepDate from './components/protocolPrepDate';
import ObservationDate from './components/observationDate';
import { notifyError, notifySuccess, notifyWarn } from '../../../components/snackbar/snackbar';
import TitleBar from '../shared/titleBar/titleBar';
import { TitleBarProps } from '../shared/titleBar/types/titleBarProps';
import { createValidationPoForm } from '../../../helpers/validation/validation';
import { ConfigField } from '../../../contexts/krFormContext';
import { getProtocolStatuses } from '../shared/titleBar/objectStatusList';
import { ObjectType } from '../../../contexts/types/card';
import ViewContext from '../../../contexts/viewContext';
import { getCodedValuesDictionaries, getTask, setCodedValuesDictionaries, setTask } from '../../../database/indexedDbReadWrite';
import { getDictionariesFromLayers, getDictionaryForCodedValues } from '../../../helpers/dictionariesFromLayers';
import { ObjectState } from '../taskListView/types/tasklListViewProps';

const PoFormView = (props: PoFormViewProps) => {
    const api = useContext(apiContext);
    const { setViewType, viewTypes, setLocalView, setViewData } = useContext(ViewContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [poForm, setPoForm] = useState<poFormTypes>(defaultPoForm());
    const [localPo] = useState<boolean>(props.data.local);
    const objectType = props.data.row.attributes.RODZAJ_OBIEKTU;
    const isLandslide = objectType === ObjectType.POO;

    const preventReload = useRef(false);

    useEffect(() => {
        if (poForm.waitingRoomGlobalId) {
            if (localPo && !saving) {
                setTask(poForm, undefined, undefined, true, true).then(() => {
                });
            }
        }
    }, [poForm, localPo, saving])
/*
    const checkLocalStorage = () => {
        return getTask(props.row?.attributes.GLOBALID).then((response) => {
            if (response) {
                return true;
            }
            else {
                return false;
            }
        }).catch(() => {
            return false;
        })
    }
*/

    useEffect(() => {
        if (!localPo) {
            const unloadCallback = (event: any) => {
                event.preventDefault();
                event.returnValue = "";
                return "";
            };

            window.addEventListener("beforeunload", unloadCallback);
            return () => window.removeEventListener("beforeunload", unloadCallback);
        }
    }, [localPo]);

    useEffect(() => {
        //prevent get data after status change when open card
        if (preventReload.current) {
            preventReload.current = false;
            return;
        };

        const objectStatus = props.data.row.attributes.STAN_OBIEKTU_DOM;
        const globalId = props.data.row?.attributes.GLOBALID;
        var statesChangingStatusForEdit = [ObjectState.Assigned, ObjectState.Complete];
        var changeStatus = statesChangingStatusForEdit.includes(objectStatus);

        if (localPo) {
            if ((objectStatus === ObjectState.DownloadedForEdition || objectStatus === ObjectState.Accepted || objectStatus === ObjectState.Complete)) {
                let newStatus = changeStatus ? ObjectState.DownloadedForEdition : undefined;

                getTask(globalId, newStatus).then((element) => {
                    if (element) {
                        var form = element?.elementParams.card as poFormTypes;
                        let actualStatus = element?.elementParams?.tableParams?.STAN_OBIEKTU_DOM;
                        if (changeStatus === false || actualStatus === newStatus) {
                            showChangeStatus(changeStatus, newStatus);

                            var config: ConfigField = {
                                Validation: createValidationPoForm(form),
                                check: false
                            };
                            form.config = config;
                            getCodedValuesDictionaries().then((codedValues) => {
                                if (!codedValues) {
                                    var authorDictId = api.featureServices.dictionariesForCodedValues.find(item => item[0] === "AUTORZY");
                                    if (authorDictId) {
                                        getDictionaryForCodedValues(api.featureServices.dictionariesForEditingBaseUrl + authorDictId[1])
                                        .then((res) => {
                                            var dictId: number = +authorDictId![1];
                                            var codedVals = getDictionariesFromLayers(res, [dictId]);
                                            setCodedValuesDictionaries(codedVals);
                                            form.dictionariesCodedValues = codedVals;
                                            setPoForm(form);
                                            setLoading(false);
                                        })
                                    }
                                }
                                else {
                                    form.dictionariesCodedValues = codedValues;
                                    setPoForm(form);
                                    setLoading(false);
                                }
                            })
                            .catch(() => {
                                populateFormError();
                            })
                        } else {
                            notifyWarn({ message: "Niepowodzenie podczas zmiany statusu" });
                            setViewType(viewTypes.TaskList);
                        }
                    }
                    else {
                        notifyWarn({ message: "Nie posiadasz tej karty w swojej bazie lokalnej" });
                        setViewType(viewTypes.TaskList);
                    }
                    setLocalView(true);
                }).catch(() => {
                    populateFormError();
                });
            } else {
                populateFormError();
            }
        } else {
            let newStatus = changeStatus ? ObjectState.WaitingRoomEdition : undefined;
            setLoading(true);
            setLocalView(false);
            
            populateProtocolForm(globalId, api.apiUrl + api.apiVersion, changeStatus, newStatus).then((response: any) => {
                showChangeStatus(changeStatus, newStatus);
                handlePopulatePoForm(response);                      
            })
            .catch(() => {
                populateFormError();
            });
        }
        return () => { }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data, api.apiUrl, api.apiVersion]);

    const showChangeStatus = (isChange: boolean, newStatus?: string) => {
        if (isChange) {
            notifySuccess({ message: `Zmieniono status na '${newStatus}'` });
            preventReload.current = true;
            let newRow = { ...props.data.row, attributes: { ...props.data.row.attributes, STAN_OBIEKTU_DOM: newStatus } }
            setViewData({...props.data, row: newRow});
        }
    }

    const handlePopulatePoForm = (response: any) => {
        var form = response as poFormTypes;
        var config: ConfigField = {
            Validation: createValidationPoForm(form),
            check: false
        };
        form.config = config;
        var authorDictId = api.featureServices.dictionariesForCodedValues.find(item => item[0] === "AUTORZY");
        if (authorDictId) {
            getDictionaryForCodedValues(api.featureServices.dictionariesForEditingBaseUrl + authorDictId[1])
            .then((res) => {
                var dictId: number = +authorDictId![1];
                form.dictionariesCodedValues = getDictionariesFromLayers(res, [dictId]);

                setPoForm(form);
                setLoading(false);
                setLocalView(false);
            })
            .catch(() => {
                populateFormError();
            });
        }
    }

    const populateFormError = () => {
        notifyError({ message: "Próba pobrania danych dokumentu zakończona niepowodzeniem" });
        setViewType(viewTypes.TaskList);
    }

    var titleBarProps: TitleBarProps = {
        title: `Formularz protokołu obserwacji ${isLandslide ? "osuwiska" : "terenu zagrożonego"}`,
        toolbar: {
            isVisible: true
        },
        isKR: false,
        loading: loading,
        setSaving: setSaving,
        objectStatesDetails: getProtocolStatuses(),
        currentRow: props.data.row,
        local: localPo
    }

    var elaboratorsSectionTitle = (
        <>
            <h2>5. Sporządzający protokół obserwacji</h2>
            <h2>6. Instytucja reprezentowana przez sporządzającego protokół obserwacji</h2>
        </>
    );

    return (
        <>
            <PoFormContext.Provider value={{ poForm, setPoForm, localPo }}>
                <TitleBar {...titleBarProps} context={poForm} setContext={setPoForm} />
                <div id="scrollIndicator" className="poFormView">
                    {(loading || saving) && <div className='spinner-po-form'><Spinner spinnerClass={ClassNameSpinner.Big} /></div>}
                    {!loading && !saving &&
                        <>
                            <div className='registrationNumber'><RegistrationNumber isLandslide={isLandslide} /></div>
                            <div className='observationConclusions'><ObservationConclusions /></div>
                            <div className='observationRecommendations'><ObservationRecommendations /></div>
                            <div className='photographicDocumentation'><PhotographicDocumentation isLandslide={isLandslide} /></div>
                            <div className='elaboratorsAndInstitutions'><ElaboratorsAndInstitutions editMode={true} objectType={objectType} sectionTitle={elaboratorsSectionTitle} context={poForm} setContext={setPoForm} isKR={false} /></div>
                            <div className='observationDate'><ObservationDate /></div>
                            <div className='protocolPrepDate'><ProtocolPrepDate /></div>
                        </>
                    }
                </div>
            </PoFormContext.Provider>
        </>
    )
}

export default PoFormView;